import { Component, Inject } from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatDialogClose,
  MatDialog,
  MatDialogRef,
  MatDialogModule,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { TranslateModule } from "@ngx-translate/core";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { AppUrlConstants } from "../../../core/constants/AppUrlConstants";
import { AlertDialogComponent } from "../../../shared/alert-dialog/alert-dialog.component";
import { AuthenticationService } from "../../../core/services/authentication.service";
import { GenericCrudService } from "../../../core/services/generic-crud.service";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatTableDataSource } from "@angular/material/table";
import { CommonModule } from "@angular/common";

interface User {
  defaultUserId: number;
  userName: string;
  segmentId: number;
}

interface Group {
  defaultAssigneeGroup: number;
  name: string;
}

@Component({
  selector: "app-complaint-subtype-add-edit-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatCheckbox,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogClose,
    TranslateModule,
    MatOptionModule,
    MatSelectModule,
  ],
  templateUrl: "./complaint-subtype-add-edit-dialog.component.html",
})
export class ComplaintSubtypeAddEditDialogComponent {
  form: FormGroup;
  complainCategoryList: any[] = [];
  filteredListCategory: any[] = [];
  userManagementList: User[] = [];
  filteredUserList: User[] = [];
  groupList: Group[] = [];
  filteredGroupList: Group[] = [];
  dataSource: MatTableDataSource<any>;
  isParentPublic: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ComplaintSubtypeAddEditDialogComponent>,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private crudService: GenericCrudService,
    private authService: AuthenticationService
  ) {}

  ngOnInit() {
    this.getByParentCategoryId();
    this.getUsers();
    this.getGroups();

    this.form = this.formBuilder.group({
      complaintCategoryId: [null],
      name: ["", [Validators.required, Validators.maxLength(60)]],
      description: ["", [Validators.required, Validators.maxLength(300)]],
      segmentId: [this.authService.getUserSegment()],
      complaintParentCategoryId: ["", [Validators.required]],
      complaintResolutionPeriod: ["", [Validators.required]],
      legalFramework: [""],
      whatsappNameEnglish: ["", [Validators.required, Validators.minLength(5), Validators.maxLength(24)]],
      whatsappNameUrdu: ["", [Validators.required, Validators.minLength(5), Validators.maxLength(24)]],
      pulse: [false],
      plra: [false],
      segmentType: [""],
      isPublic: [false],
      defaultUserId:[null,[Validators.required]],
      defaultAssigneeGroup: [null],
    });

    // Initialize fields based on the values of pulse and plra
    const isEnabled = this.form.value.pulse || this.form.value.plra;
    if (!isEnabled) {
      this.form.get('defaultUserId')?.disable();
      this.form.get('defaultAssigneeGroup')?.disable();
    }

    // Update fields if data is passed for editing
    if (this.data != null) {
      this.updateSegment(this.data);
      this.form.patchValue({
        complaintCategoryId: this.data?.complaintCategoryId,
        complaintParentCategoryId: this.data?.complaintParentCategoryId ?? null,
        name: this.data?.name,
        description: this.data?.description,
        segmentId: this.data?.segmentId,
        complaintResolutionPeriod: this.data?.complaintResolutionPeriod,
        legalFramework: this.data?.legalFramework,
        whatsappNameEnglish: this.data?.whatsappNameEnglish,
        whatsappNameUrdu: this.data?.whatsappNameUrdu,
        pulse: this.data?.pulse,
        plra: this.data?.plra,
        segmentType: this.data?.segmentType,
        isPublic: this.data?.isPublic ?? false,
        defaultUserId: this.data?.defaultUserId ?? null,
        defaultAssigneeGroup: this.data?.defaultAssigneeGroup ?? null,
      });

      // Update the disabled state after patching
      const updatedIsEnabled = this.form.value.pulse || this.form.value.plra;
      if (!updatedIsEnabled) {
        this.form.get('defaultUserId')?.disable();
        this.form.get('defaultAssigneeGroup')?.disable();
      } else {
        this.form.get('defaultUserId')?.enable();
        this.form.get('defaultAssigneeGroup')?.enable();
      }
    }
  }


  onPulseChange(pulse: boolean): void {
    this.handleSegmentChange(pulse, this.form.value.plra, 1); // Segment ID 1 for Pulse
  }

  onPlraChange(plra: boolean): void {
    this.handleSegmentChange(this.form.value.pulse, plra, 2); // Segment ID 2 for PLRA
  }

  private handleSegmentChange(pulse: boolean, plra: boolean, segmentId: number): void {
    if (!pulse && !plra) {
      this.clearPulsePlraFields(); // Clear fields when both are unchecked
    } else {
      this.updateUserFilters(); // Update user filters
      this.form.get("defaultUserId")?.enable();
      this.form.get("defaultAssigneeGroup")?.enable();
    }

    if (pulse || plra) {
      this.form.patchValue({
        segmentId: segmentId, // Set segment ID based on the active checkbox
        defaultUserId: null, // Ensure no user is preselected
        defaultAssigneeGroup: null // Ensure no group is preselected
      });
    }
  }



  clearPulsePlraFields() {
    this.form.get("defaultUserId")?.disable();
    this.form.get("defaultAssigneeGroup")?.disable();
    this.form.patchValue({
      defaultUserId: null,
      defaultAssigneeGroup: null,
      segmentId: null,
    });
    this.filteredUserList = [];
    this.filteredGroupList = [...this.groupList];
  }

  updateUserFilters() {
    if (this.form.value.pulse && this.form.value.plra) {
      // Show all users if both Pulse and PLRA are selected
      this.filteredUserList = [...this.userManagementList];
    } else if (this.form.value.pulse) {
      // Filter users by segmentId = 1 for Pulse
      this.filteredUserList = this.userManagementList.filter(
        (user) => user.segmentId === 1
      );
    } else if (this.form.value.plra) {
      // Filter users by segmentId = 2 for PLRA
      this.filteredUserList = this.userManagementList.filter(
        (user) => user.segmentId === 2
      );
    }
  }

  getDefaultPulseUser(): number | null {
    return (
      this.userManagementList.find((user) => user.segmentId === 1)
        ?.defaultUserId || null
    );
  }

  getDefaultPlraUser(): number | null {
    return (
      this.userManagementList.find((user) => user.segmentId === 2)
        ?.defaultUserId || null
    );
  }

  getUsers() {
    this.crudService
      .getAll(AppUrlConstants.USER_URL + AppUrlConstants.LIST)
      .subscribe({
        next: (res: any) => {
          this.userManagementList =
            res?.data?.map((user: any) => ({
              defaultUserId: user.userId,
              userName: user.userName,
              segmentId: user.segmentId,
            })) || [];
          this.filteredUserList = [...this.userManagementList];
        },
        error: (err) => console.error("Error fetching user list:", err),
      });
  }

  getGroups() {
    this.crudService
      .getAll(AppUrlConstants.GROUP_URL + AppUrlConstants.LIST)
      .subscribe({
        next: (res: any) => {
          this.groupList =
            res?.data
              ?.filter((group: any) => group.groupType?.toLowerCase().trim() === "committee")
              .map((group: any) => ({
                defaultAssigneeGroup: group.groupId,
                name: group.name,
              })) || [];
          this.filteredGroupList = [...this.groupList];
        },
        error: (err) => console.error("Error fetching group list:", err),
      });
  }

  getByParentCategoryId() {
    this.crudService
      .getById(
        1,
        AppUrlConstants.COMPLAINT_CATEGORY_URL + AppUrlConstants.SEGMENT
      )
      .subscribe({
        next: (res: any) => {
          this.complainCategoryList = res?.data || [];
          this.filteredListCategory = [...this.complainCategoryList];
        },
        error: (err) =>
          console.error("Error fetching complaint categories:", err),
      });
  }

  add() {
    this.form.patchValue({ segmentType: this.checkSegment(this.form.value) });
    this.crudService
      .add(
        this.form.value,
        AppUrlConstants.COMPLAINT_CATEGORY_URL + AppUrlConstants.ADD
      )
      .subscribe({
        next: (res: any) => this.dialogRef.close(res?.data),
        error: (e) => this.handleError(e),
      });
  }

  edit() {
    this.form.patchValue({ segmentType: this.checkSegment(this.form.value) });
    this.crudService
      .edit(
        this.form.value,
        AppUrlConstants.COMPLAINT_CATEGORY_URL + AppUrlConstants.EDIT
      )
      .subscribe({
        next: (res: any) => this.dialogRef.close(res?.data),
        error: (e) => this.handleError(e),
      });
  }

  handleError(e: any) {
    if (e?.status === 409 && e?.error?.errors) {
      for (const property in e.error.errors) {
        this.openAlert(e.error.errors[property]);
      }
    } else {
      this.openAlert(e?.error?.message || "An unexpected error occurred.");
    }
  }

  openAlert(message: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: "OK",
        },
      },
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  checkSegment(result: any) {
    if (result.pulse && result.plra) {
      return "ALL";
    } else if (!result.pulse && !result.plra) {
      return "NONE";
    } else {
      return result.pulse ? "PULSE" : "PLRA";
    }
  }

  updateSegment(updateRecord: any) {
    switch (updateRecord?.segmentType) {
      case "ALL":
        updateRecord.pulse = true;
        updateRecord.plra = true;
        break;
      case "NONE":
        updateRecord.pulse = false;
        updateRecord.plra = false;
        break;
      case "PULSE":
        updateRecord.pulse = true;
        updateRecord.plra = false;
        break;
      case "PLRA":
        updateRecord.pulse = false;
        updateRecord.plra = true;
        break;
      default:
        break;
    }
  }

  onFilteredListSearch(
    event: Event,
    originalList: any[],
    property: string
  ): any[] {
    const searchValue = (
      event.target as HTMLInputElement
    )?.value?.toLowerCase();
    return originalList.filter((item) =>
      item[property]?.toLowerCase().includes(searchValue)
    );
  }
}
